import { getBlockContent, type StreamingBlock, type StreamingChatId } from "@storytell/streaming";
import {
  TbAlertTriangle,
  TbArrowRight,
  TbBolt,
  TbBrandGithub,
  TbBrandSlack,
  TbCalendarEvent,
  TbCheck,
  TbChevronRight,
  TbClock,
  TbExternalLink,
  TbFile,
  TbFolder,
  TbLoader2,
  TbMail,
  TbMapPin,
  TbPlug,
  TbRepeat,
  TbShieldCheck,
  TbTag,
  TbTrash,
  TbUsers,
  TbVideo,
  TbX,
} from "solid-icons/tb";
import { createEffect, createMemo, createSignal, For, Match, onMount, Show, Switch } from "solid-js";
import { twMerge } from "tailwind-merge";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { type ChatRuntime, useChatRuntime } from "../runtime/ChatRuntime";
import { formatIsoTimestamp } from "../util/dateFormat";
import { extractErrorInfo } from "../util/errorInfo";
import { formatDay, formatTime, isSameDay } from "./calendarRenderUtils";
import { ConfirmActionEdit } from "./ConfirmActionEdit";
import {
  type ActionConfigAction,
  type ActionDryRunAction,
  type ActionSchedulePreview,
  type ActionSetEnabledAction,
  type AssetUpdateAction,
  type AssetUpdatePreview,
  type CalendarAction,
  type ChiefDeleteAction,
  type ConfirmActionPayload,
  type ConfirmActionStatus,
  type GDriveCreateDocAction,
  type GDriveUpdateDocAction,
  type GitHubAddCommentAction,
  type GitHubCreateIssueAction,
  type GitHubUpdateIssueAction,
  type GmailDraftDeleteAction,
  type GmailDraftUpdateAction,
  type GmailTrashAction,
  isActionConfigAction,
  isChiefAction,
  isChiefActionsAction,
  isChiefDeleteAction,
  isGDriveAction,
  isGitHubAction,
  isGmailAction,
  isLinearAction,
  isMCPAction,
  isNotionAction,
  isSlackAction,
  isSlackTextAction,
  type LabelAssignAction,
  type LabelCreateAction,
  type LabelUnassignAction,
  type LinearAddCommentAction,
  type LinearCreateIssueAction,
  type LinearUpdateIssueAction,
  type MCPCallAction,
  type MoveAction,
  type NotionAddCommentAction,
  type NotionBlockSpec,
  type NotionCreateDatabaseAction,
  type NotionCreateDbRowAction,
  type NotionCreatePageAction,
  type NotionDatabaseColumnSpec,
  type NotionEditSpec,
  type NotionMovePageAction,
  type NotionPropertySpec,
  type NotionUpdateDatabaseAction,
  type NotionUpdateDbRowAction,
  type NotionUpdatePageAction,
  parseConfirmActionPayload,
  type RsvpAction,
  type RsvpResponse,
  type SendEmailAction,
  type SlackReactionAction,
  type SlackReplyAction,
  type SlackSendDmAction,
  type SlackSendMessageAction,
} from "./confirmActionSchema";
import { senderDisplayName } from "./emailRenderUtils";
import { DriveIcon } from "./gdrive/driveIcon";
import { safeGitHubUrl } from "./github/githubRenderUtils";
import { CompleteMarkdown } from "./internal/CompleteMarkdown";
import { LinearIcon } from "./linear/linearIcon";
import { priorityLabelKey, safeLinearUrl } from "./linear/linearRenderUtils";
import { NotionIcon } from "./notion/notionIcon";
import { SlackMessageText } from "./slack/slackRenderUtils";
import { type ToolActionServerStatus, useChatToolActions, useToolActionConfirm } from "./useToolActions";

interface ConfirmActionRendererProps {
  block: StreamingBlock;
  chatId: StreamingChatId;
  // Every parseable action is hoisted into the pinned section, so "timeline"
  // only renders blocks that hoisting skipped; it suppresses the Edit control
  // the pinned card offers.
  surface?: "timeline" | "pinned";
  // Drops the card's own border/background so it reads as content inside an
  // enclosing group rather than a nested box.
  flat?: boolean;
}

const safeNotionUrl = (url: string | undefined): string | undefined => (url?.startsWith("https://") ? url : undefined);

// A write through a tool's MCP server links to that tool only when its record
// names the tool's own https host, so one link never reads as another tool's.
const NOTION_HOSTS = ["notion.so", "www.notion.so"];
const LINEAR_HOSTS = ["linear.app"];
const GITHUB_HOSTS = ["github.com"];
export const mcpUrlOn = (url: string | undefined, hosts: readonly string[]): string | undefined => {
  if (!url) return undefined;
  try {
    const u = new URL(url);
    return u.protocol === "https:" && !u.port && !u.username && hosts.includes(u.hostname.toLowerCase())
      ? url
      : undefined;
  } catch {
    return undefined;
  }
};

// Saved permissions are undone in the tool-permission settings, so the note links there.
const PermissionNote = (props: { text: string }) => {
  const rt = useChatRuntime();
  return (
    <span class="inline-flex max-w-full min-w-0 items-center gap-1 text-xs text-muted-text">
      <TbShieldCheck class="size-3.5 shrink-0" aria-hidden="true" />
      <span class="truncate">{props.text}</span>
      <Show when={rt.actions.openToolPermissions}>
        <span aria-hidden="true">·</span>
        <button
          type="button"
          onClick={() => rt.actions.openToolPermissions?.()}
          class="shrink-0 rounded font-medium text-secondary-text transition-colors hover:text-primary-text hover:underline focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
        >
          {rt.t("confirm_action_card.manage")}
        </button>
      </Show>
    </span>
  );
};

const McpReconnectButton = (props: { serverId: string; serverName: string }) => {
  const rt = useChatRuntime();
  const conn = rt.ui?.useMcpConnect?.();

  return (
    <Show when={conn}>
      {(c) => (
        <span class="inline-flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              rt.actions.trackFeature?.({ feature: "chat.connect_provider", action: "mcp", via: "button" });
              void c().connect(props.serverId);
            }}
            disabled={c().isConnecting()}
            aria-busy={c().isConnecting()}
            class="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-brand px-2.5 py-1 text-xs font-semibold text-on-brand transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Show
              when={c().isConnecting()}
              fallback={
                <>
                  <TbPlug class="size-3.5 shrink-0" />
                  {rt.t("confirm_action_card.mcp_call.reconnect", { server: props.serverName })}
                </>
              }
            >
              <TbLoader2 class="size-3.5 shrink-0 animate-spin" />
              {rt.t("common.connecting")}
            </Show>
          </button>
          <Show when={c().error?.()}>
            {(message) => <span class="truncate text-xs text-red-500">{message()}</span>}
          </Show>
        </span>
      )}
    </Show>
  );
};

// The block content is final by the time the card renders, so a one-shot
// measurement at mount is enough to know whether the clamp hides anything.
const NotionMarkdownPreview = (props: { text: string }) => {
  const t = useChatRuntime().t;
  const [expanded, setExpanded] = createSignal(false);
  const [clamped, setClamped] = createSignal(false);
  let textRef: HTMLParagraphElement | undefined;

  onMount(() => {
    if (textRef) setClamped(textRef.scrollHeight > textRef.clientHeight);
  });

  return (
    <div class="mt-1">
      <Show
        when={expanded()}
        fallback={
          <p ref={textRef} class="line-clamp-6 text-xs wrap-break-word whitespace-pre-wrap text-muted-text">
            {props.text}
          </p>
        }
      >
        <CompleteMarkdown markdown={props.text} />
      </Show>
      <Show when={clamped()}>
        <button
          type="button"
          onClick={() => setExpanded(!expanded())}
          aria-expanded={expanded()}
          class="mt-0.5 text-xs font-medium text-brand hover:underline focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
        >
          {expanded() ? t("common.show_less") : t("common.show_more")}
        </button>
      </Show>
    </div>
  );
};

const notionBlocksText = (blocks: NotionBlockSpec[] | undefined): string =>
  (blocks ?? [])
    .map((b) => b.text)
    .filter(Boolean)
    .join("\n");

// Property values are model-generated and rendered as text. Each Notion type
// decodes to a different JSON shape, flattened here to one line.
const notionPropText = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.filter((x): x is string => typeof x === "string").join(", ");
  if (value && typeof value === "object") {
    const date = value as { start?: unknown; end?: unknown };
    if (typeof date.start === "string") {
      return typeof date.end === "string" && date.end ? `${date.start} – ${date.end}` : date.start;
    }
  }
  return "";
};

const NotionProperties = (props: { properties: Record<string, NotionPropertySpec> }) => {
  const t = useChatRuntime().t;
  return (
    <div class="space-y-0.5">
      <p class="text-xs font-medium text-muted-text">{t("confirm_action_card.notion_properties_label")}</p>
      <ul class="space-y-0.5">
        <For each={Object.entries(props.properties)}>
          {([name, spec]) => (
            <li class="truncate text-xs text-muted-text">
              <span class="text-primary-text">{name}</span>
              <Show when={notionPropText(spec.value)}>{(val) => <span>: {val()}</span>}</Show>
            </li>
          )}
        </For>
      </ul>
    </div>
  );
};

// The value when it has entries, so one <Show> both skips an empty preview field and narrows it.
const nonEmpty = <T extends object>(value: T | undefined): T | undefined =>
  value && Object.keys(value).length > 0 ? value : undefined;

// Database columns are Notion "properties"; labelKey distinguishes added
// columns from a full schema.
const NotionColumns = (props: { columns: Record<string, NotionDatabaseColumnSpec>; labelKey?: string }) => {
  const t = useChatRuntime().t;
  return (
    <div class="space-y-0.5">
      <p class="text-xs font-medium text-muted-text">
        {t(props.labelKey ?? "confirm_action_card.notion_properties_label")}
      </p>
      <ul class="space-y-0.5">
        <For each={Object.entries(props.columns)}>
          {([name, spec]) => (
            <li class="truncate text-xs text-muted-text">
              <span class="text-primary-text">{name}</span>: {spec.type}
              <Show when={nonEmpty(spec.options)}>{(options) => <span> ({options().join(", ")})</span>}</Show>
            </li>
          )}
        </For>
      </ul>
    </div>
  );
};

const NOTION_ROWS_PREVIEW_LIMIT = 3;

// Initial row data seeded into a new database, distinct from the column schema:
// each row is one line of `column: value` pairs, capped to a short preview.
const NotionRows = (props: { rows: Array<Record<string, NotionPropertySpec>> }) => {
  const t = useChatRuntime().t;
  const shown = () => props.rows.slice(0, NOTION_ROWS_PREVIEW_LIMIT);
  const remaining = () => props.rows.length - shown().length;
  return (
    <div class="space-y-0.5">
      <p class="text-xs font-medium text-muted-text">
        {t("confirm_action_card.notion_rows_label")} ({props.rows.length})
      </p>
      <ul class="space-y-0.5">
        <For each={shown()}>
          {(row) => (
            <li class="truncate text-xs text-muted-text">
              <For each={Object.entries(row)}>
                {([name, spec], i) => (
                  <>
                    <Show when={i() > 0}>{", "}</Show>
                    <span class="text-primary-text">{name}</span>
                    <Show when={notionPropText(spec.value)}>{(val) => <span>: {val()}</span>}</Show>
                  </>
                )}
              </For>
            </li>
          )}
        </For>
      </ul>
      <Show when={remaining() > 0}>
        <p class="text-xs text-muted-text">
          {t("confirm_action_card.notion_rows_more", { count: String(remaining()) })}
        </p>
      </Show>
    </div>
  );
};

// A struck-through old string with no new string is a deletion.
const NotionEdits = (props: { edits: NotionEditSpec[] }) => {
  const t = useChatRuntime().t;
  return (
    <div class="space-y-1">
      <p class="text-xs font-medium text-muted-text">
        {t("confirm_action_card.notion_edits_label")} ({props.edits.length})
      </p>
      <ul class="space-y-1.5">
        <For each={props.edits}>
          {(edit) => (
            <li class="space-y-0.5">
              <p class="line-clamp-2 text-xs wrap-break-word whitespace-pre-wrap text-muted-text line-through">
                {edit.old_str}
              </p>
              <Show when={edit.new_str}>
                <p class="line-clamp-2 text-xs wrap-break-word whitespace-pre-wrap text-primary-text">{edit.new_str}</p>
              </Show>
              <Show when={edit.replace_all}>
                <p class="text-xs text-muted-text">{t("confirm_action_card.notion_edit_replace_all")}</p>
              </Show>
            </li>
          )}
        </For>
      </ul>
    </div>
  );
};

// Dry-run results link back into the app; anything that isn't an in-app path
// or a same-origin http(s) URL is dropped rather than rendered.
const safeChatUrl = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  if (url.startsWith("/")) return url;
  try {
    const parsed = new URL(url);
    if (
      (parsed.protocol === "http:" || parsed.protocol === "https:") &&
      typeof location !== "undefined" &&
      parsed.origin === location.origin
    ) {
      return url;
    }
  } catch {
    return undefined;
  }
  return undefined;
};

const ActionViewEditLinks = (props: { url: string }) => {
  const t = useChatRuntime().t;
  const editHref = () => `${props.url}${props.url.includes("?") ? "&" : "?"}edit=true`;
  return (
    <>
      <a
        href={props.url}
        class="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-brand hover:underline"
      >
        {t("confirm_action_card.view_action")}
        <TbExternalLink class="size-3 shrink-0" aria-hidden="true" />
      </a>
      <a
        href={editHref()}
        class="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-brand hover:underline"
      >
        {t("confirm_action_card.edit_action")}
        <TbExternalLink class="size-3 shrink-0" aria-hidden="true" />
      </a>
    </>
  );
};

const isLabelKind = (p: ConfirmActionPayload): boolean =>
  p.actionKind === "label_create" ||
  p.actionKind === "smart_tag_create" ||
  p.actionKind === "label_assign" ||
  p.actionKind === "label_unassign" ||
  p.actionKind === "label_delete";

const isAssetKind = (p: ConfirmActionPayload): boolean =>
  p.actionKind === "asset_update" || p.actionKind === "asset_delete";

const PlainTextPreview = (props: { text: string }) => {
  const rt = useChatRuntime();
  const t = rt.t;
  const [expanded, setExpanded] = createSignal(false);
  const [clamped, setClamped] = createSignal(false);
  let textRef: HTMLParagraphElement | undefined;

  onMount(() => {
    if (textRef) setClamped(textRef.scrollHeight > textRef.clientHeight);
  });

  return (
    <div class="mt-1">
      <Show
        when={expanded()}
        fallback={
          <p ref={textRef} class="line-clamp-6 text-xs wrap-break-word whitespace-pre-wrap text-muted-text">
            {props.text}
          </p>
        }
      >
        <p class="text-xs wrap-break-word whitespace-pre-wrap text-muted-text">{props.text}</p>
      </Show>
      <Show when={clamped()}>
        <button
          type="button"
          onClick={() => setExpanded(!expanded())}
          aria-expanded={expanded()}
          class="mt-0.5 text-xs font-medium text-brand hover:underline focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
        >
          {expanded() ? t("common.show_less") : t("common.show_more")}
        </button>
      </Show>
    </div>
  );
};

/**
 * Names the cadence the action will actually run on. An interval and a wildcard
 * hour each resolve ahead of the weekday and the month day and never consult
 * either, so both are the whole schedule: naming a day beside them would
 * describe runs that never happen.
 */
export const scheduleSummary = (s: ActionSchedulePreview, t: ChatRuntime["t"]): string => {
  const withZone = (text: string): string => (s.timezone ? `${text} · ${s.timezone}` : text);

  const interval = s.minutes ?? 0;
  if (interval > 0) {
    // Whole hours read as hours; everything else stays in minutes.
    return withZone(
      interval % 60 === 0 && interval >= 120
        ? t("confirm_action_card.action_schedule_interval_hours", { hours: String(interval / 60) })
        : t("confirm_action_card.action_schedule_interval", { minutes: String(interval) })
    );
  }

  if (s.hour.trim() === "*") return withZone(t("confirm_action_card.action_schedule_hourly"));

  const hour = `${s.hour.trim()}:00`;
  if (s.weekday && s.weekday !== "*") {
    return withZone(t("confirm_action_card.action_schedule_weekly", { weekday: s.weekday, hour }));
  }
  if (s.monthDay && s.monthDay !== "*") {
    return withZone(t("confirm_action_card.action_schedule_monthly", { monthDay: s.monthDay, hour }));
  }
  return withZone(t("confirm_action_card.action_schedule_daily", { hour }));
};

const ChiefActionConfig = (props: { action: ActionConfigAction; pending: boolean }) => {
  const rt = useChatRuntime();
  const t = rt.t;
  const preview = () => props.action.preview;

  const scopeCount = (): number => {
    const s = preview().scope;
    if (!s) return 0;
    return (
      (s.assetIds?.length ?? 0) + (s.labelIds?.length ?? 0) + (s.conceptIds?.length ?? 0) + (s.viewIds?.length ?? 0)
    );
  };

  const autoApproveSummary = (ops: string[]): string =>
    ops
      .map((op) => {
        const key = rt.ui?.autoApproveOpLabelKey?.(op);
        return key ? t(key) : op;
      })
      .join(", ");

  return (
    <div class="min-w-0 flex-1 space-y-1 self-start">
      <Show when={props.action.actionKind === "action_update"}>
        <p class="truncate text-xs text-muted-text">
          {t("confirm_action_card.action_updating_label", {
            name: preview().actionName ?? preview().actionId ?? "",
          })}
        </p>
      </Show>
      <p class="truncate text-sm font-bold text-primary-text">{preview().name}</p>
      <Show when={preview().description}>
        <p class="line-clamp-2 text-xs wrap-break-word whitespace-pre-wrap text-muted-text">{preview().description}</p>
      </Show>
      <PlainTextPreview text={preview().prompt} />
      <Show when={preview().schedule}>
        {(schedule) => (
          <span class="inline-flex max-w-full min-w-0 items-center gap-1 text-xs text-muted-text">
            <TbClock class="size-3.5 shrink-0" />
            <span class="truncate">{scheduleSummary(schedule(), t)}</span>
          </span>
        )}
      </Show>
      <Show when={preview().trigger}>
        {(trigger) => (
          <span class="inline-flex items-center gap-1 text-xs text-muted-text">
            <TbBolt class="size-3.5 shrink-0" />
            {t(
              trigger().kind === "new"
                ? "confirm_action_card.action_trigger_new"
                : "confirm_action_card.action_trigger_all"
            )}
          </span>
        )}
      </Show>
      <Show when={preview().email}>
        {(email) => (
          <span class="inline-flex max-w-full min-w-0 items-center gap-1 text-xs text-muted-text">
            <TbMail class="size-3.5 shrink-0" />
            <span class="truncate">
              {t("confirm_action_card.action_email_line", { recipients: email().to.join(", ") })}
            </span>
          </span>
        )}
      </Show>
      <Show when={scopeCount() > 0}>
        <span class="inline-flex items-center gap-1 text-xs text-muted-text">
          <TbFolder class="size-3.5 shrink-0" />
          {t("confirm_action_card.action_scope_line", { count: String(scopeCount()) })}
        </span>
      </Show>
      <Show when={(preview().autoApprove?.length ?? 0) > 0}>
        {/* The grants are the only part of the card that outlives this one run,
            so a card still awaiting a decision states them at warning weight. */}
        <p
          class={twMerge(
            "flex min-w-0 items-start gap-1.5 text-xs text-muted-text",
            props.pending && "rounded-md border border-primary-border bg-panel px-2.5 py-1.5 font-medium text-red-500"
          )}
        >
          <TbShieldCheck class="mt-px size-3.5 shrink-0" />
          <span class="min-w-0 wrap-break-word">
            {t("confirm_action_card.action_auto_approve_line", {
              operations: autoApproveSummary(preview().autoApprove ?? []),
            })}
          </span>
        </p>
      </Show>
    </div>
  );
};

const ChiefLabelAssets = (props: { action: LabelAssignAction | LabelUnassignAction }) => {
  const rt = useChatRuntime();
  const t = rt.t;
  const label = () => props.action.preview.labelName ?? props.action.preview.labelId;
  const count = () => props.action.preview.assets.length;
  const summary = (): string => {
    const unassign = props.action.actionKind === "label_unassign";
    if (count() === 1) {
      return t(
        unassign ? "confirm_action_card.label_unassign_summary_one" : "confirm_action_card.label_assign_summary_one",
        { label: label() }
      );
    }
    return t(unassign ? "confirm_action_card.label_unassign_summary" : "confirm_action_card.label_assign_summary", {
      label: label(),
      count: String(count()),
    });
  };
  return (
    <div class="min-w-0 flex-1 space-y-2 self-start">
      <p class="text-sm text-primary-text">{summary()}</p>
      <Show when={count() > 0}>
        <ul class="max-h-64 divide-y divide-primary-border overflow-y-auto rounded-md border border-primary-border">
          <For each={props.action.preview.assets}>
            {(asset) => (
              <li class="px-2.5 py-1.5">
                <p class="truncate text-sm text-primary-text">{asset.name || asset.id}</p>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </div>
  );
};

const ChiefDelete = (props: { action: ChiefDeleteAction }) => {
  const rt = useChatRuntime();
  const t = rt.t;
  const name = (): string => {
    const p = props.action;
    if (p.actionKind === "action_delete") return p.preview.actionName ?? p.preview.actionId;
    if (p.actionKind === "label_delete") return p.preview.labelName ?? p.preview.labelId;
    return p.preview.assetName ?? p.preview.assetId;
  };
  const warningKey = (): string => {
    if (props.action.actionKind === "action_delete") return "confirm_action_card.delete_action_warning";
    if (props.action.actionKind === "label_delete") return "confirm_action_card.delete_label_warning";
    return "confirm_action_card.delete_asset_warning";
  };
  return (
    <div class="min-w-0 flex-1 space-y-1 self-start">
      <p class="truncate text-sm font-bold text-primary-text">{name()}</p>
      <p class="inline-flex items-center gap-1 text-xs text-red-500">
        <TbAlertTriangle class="size-3.5 shrink-0" />
        {t(warningKey())}
      </p>
    </div>
  );
};

const ChiefAssetUpdate = (props: { preview: AssetUpdatePreview }) => {
  const rt = useChatRuntime();
  const t = rt.t;
  return (
    <div class="min-w-0 flex-1 space-y-1 self-start">
      <p class="truncate text-xs text-muted-text">
        {t("confirm_action_card.asset_label")}{" "}
        <span class="text-primary-text">{props.preview.assetName ?? props.preview.assetId}</span>
      </p>
      <Show when={props.preview.name}>
        {(name) => (
          <div class="space-y-0.5">
            <Show when={props.preview.assetName}>
              <p class="line-clamp-2 text-xs wrap-break-word whitespace-pre-wrap text-muted-text line-through">
                {props.preview.assetName}
              </p>
            </Show>
            <p class="line-clamp-2 text-xs wrap-break-word whitespace-pre-wrap text-primary-text">{name()}</p>
          </div>
        )}
      </Show>
      <Show when={props.preview.description}>
        {(description) => (
          <div class="space-y-0.5">
            <p class="text-xs font-medium text-muted-text">{t("confirm_action_card.asset_update_description_label")}</p>
            <p class="line-clamp-2 text-xs wrap-break-word whitespace-pre-wrap text-primary-text">{description()}</p>
          </div>
        )}
      </Show>
    </div>
  );
};

const ChangeLine = (props: { label: string; value: string | undefined }) => {
  const t = useChatRuntime().t;

  return (
    <Show when={props.value}>
      {(value) => (
        <p class="truncate text-xs text-muted-text">
          {t(props.label)} <span class="text-primary-text">{value()}</span>
        </p>
      )}
    </Show>
  );
};

const titleKey = (payload: ConfirmActionPayload): string => {
  switch (payload.actionKind) {
    case "send_email":
      return "confirm_action_card.title_send_email";
    case "calendar_create":
      return "confirm_action_card.title_calendar_create";
    case "calendar_update":
      return "confirm_action_card.title_calendar_update";
    case "calendar_delete":
      return "confirm_action_card.title_calendar_delete";
    case "calendar_rsvp":
      return "confirm_action_card.title_calendar_rsvp";
    case "calendar_move":
      return "confirm_action_card.title_calendar_move";
    case "gmail_trash":
      return payload.preview.untrash
        ? "confirm_action_card.title_gmail_untrash"
        : "confirm_action_card.title_gmail_trash";
    case "gmail_draft_update":
      return "confirm_action_card.title_gmail_draft_update";
    case "gmail_draft_delete":
      return "confirm_action_card.title_gmail_draft_delete";
    case "send_message":
      return "confirm_action_card.title_slack_send_message";
    case "send_dm":
      return "confirm_action_card.title_slack_send_dm";
    case "reply_thread":
      return "confirm_action_card.title_slack_reply_in_thread";
    case "add_reaction":
      return "confirm_action_card.title_slack_add_reaction";
    case "notion_create_page":
      return "confirm_action_card.title_notion_create_page";
    case "notion_update_page":
      return "confirm_action_card.title_notion_update_page";
    case "notion_create_db_row":
      return "confirm_action_card.title_notion_create_db_row";
    case "notion_update_db_row":
      return "confirm_action_card.title_notion_update_db_row";
    case "notion_create_database":
      return "confirm_action_card.title_notion_create_database";
    case "notion_add_comment":
      return "confirm_action_card.title_notion_add_comment";
    case "notion_update_database":
      return "confirm_action_card.title_notion_update_database";
    case "notion_move_page":
      return "confirm_action_card.title_notion_move_page";
    case "gdrive_create_doc":
      return "confirm_action_card.title_gdrive_create_doc";
    case "gdrive_update_doc":
      return "confirm_action_card.title_gdrive_update_doc";
    case "linear_create_issue":
      return "confirm_action_card.title_linear_create_issue";
    case "linear_update_issue":
      return "confirm_action_card.title_linear_update_issue";
    case "linear_add_comment":
      return "confirm_action_card.title_linear_add_comment";
    case "github_create_issue":
      return "confirm_action_card.title_github_create_issue";
    case "github_update_issue":
      return "confirm_action_card.title_github_update_issue";
    case "github_add_comment":
      return "confirm_action_card.title_github_add_comment";
    case "mcp_call_user":
    case "mcp_call_org":
      return "confirm_action_card.mcp_call.title";
    case "action_create":
      return "confirm_action_card.title_action_create";
    case "action_update":
      return "confirm_action_card.title_action_update";
    case "action_set_enabled":
      return payload.preview.enabled
        ? "confirm_action_card.title_action_enable"
        : "confirm_action_card.title_action_disable";
    case "action_delete":
      return "confirm_action_card.title_action_delete";
    case "action_dry_run":
      return "confirm_action_card.title_action_dry_run";
    case "label_create":
      return "confirm_action_card.title_label_create";
    case "smart_tag_create":
      return "confirm_action_card.title_smart_tag_create";
    case "label_assign":
      return "confirm_action_card.title_label_assign";
    case "label_unassign":
      return "confirm_action_card.title_label_unassign";
    case "label_delete":
      return "confirm_action_card.title_label_delete";
    case "asset_update":
      return "confirm_action_card.title_asset_update";
    case "asset_delete":
      return "confirm_action_card.title_asset_delete";
  }
};

const RSVP_RESPONSE_KEY: Record<RsvpResponse, string> = {
  accepted: "confirm_action_card.rsvp_accepted",
  declined: "confirm_action_card.rsvp_declined",
  tentative: "confirm_action_card.rsvp_tentative",
};

// Keyed by the tool-approval preference the server reports for the action, so
// the copy names exactly what approve_always turns on.
const ALWAYS_ALLOW_KEYS: Partial<Record<string, { button: string; saved: string }>> = {
  "notion.write": {
    button: "confirm_action_card.always_allow_notion",
    saved: "confirm_action_card.always_allowed_notion",
  },
  "slack.send": {
    button: "confirm_action_card.always_allow_slack_send",
    saved: "confirm_action_card.always_allowed_slack_send",
  },
  "slack.dm": {
    button: "confirm_action_card.always_allow_slack_dm",
    saved: "confirm_action_card.always_allowed_slack_dm",
  },
  "slack.reply": {
    button: "confirm_action_card.always_allow_slack_reply",
    saved: "confirm_action_card.always_allowed_slack_reply",
  },
  "slack.react": {
    button: "confirm_action_card.always_allow_slack_react",
    saved: "confirm_action_card.always_allowed_slack_react",
  },
  "gmail.send": {
    button: "confirm_action_card.always_allow_gmail_send",
    saved: "confirm_action_card.always_allowed_gmail_send",
  },
  "gmail.modify": {
    button: "confirm_action_card.always_allow_gmail_modify",
    saved: "confirm_action_card.always_allowed_gmail_modify",
  },
  "gmail.draft": {
    button: "confirm_action_card.always_allow_gmail_draft",
    saved: "confirm_action_card.always_allowed_gmail_draft",
  },
  "gcalendar.write": {
    button: "confirm_action_card.always_allow_gcalendar",
    saved: "confirm_action_card.always_allowed_gcalendar",
  },
  "gdrive.write": {
    button: "confirm_action_card.always_allow_gdrive",
    saved: "confirm_action_card.always_allowed_gdrive",
  },
  "linear.write": {
    button: "confirm_action_card.always_allow_linear",
    saved: "confirm_action_card.always_allowed_linear",
  },
  "github.write": {
    button: "confirm_action_card.always_allow_github",
    saved: "confirm_action_card.always_allowed_github",
  },
  "actions.write": {
    button: "confirm_action_card.always_allow_actions",
    saved: "confirm_action_card.always_allowed_actions",
  },
  "labels.write": {
    button: "confirm_action_card.always_allow_labels",
    saved: "confirm_action_card.always_allowed_labels",
  },
  "assets.write": {
    button: "confirm_action_card.always_allow_assets",
    saved: "confirm_action_card.always_allowed_assets",
  },
};

const MCP_WRITE_OP = /^mcp\.[^.]+\.write$/;

// An MCP key names a customer's server, which no catalog entry can hold, so the
// card's own server name fills it in.
export const alwaysAllowLabels = (
  op: string,
  mcpServerName: string | undefined,
  t: (key: string, params?: Record<string, string>) => string
): { button: string; saved: string } => {
  if (mcpServerName && MCP_WRITE_OP.test(op)) {
    return {
      button: t("confirm_action_card.always_allow_mcp", { server: mcpServerName }),
      saved: t("confirm_action_card.always_allowed_mcp", { server: mcpServerName }),
    };
  }
  const keys = ALWAYS_ALLOW_KEYS[op];
  return {
    button: t(keys?.button ?? "confirm_action_card.always_allow"),
    saved: t(keys?.saved ?? "confirm_action_card.always_allowed"),
  };
};

// "running" is an approved action whose job hasn't reported back yet; "handled"
// covers a resolved action with no distinct terminal: a blocked automation, or
// a confirm 409.
type CardStatus = "pending" | "running" | "executed" | "rejected" | "failed" | "handled";

const CONFLICT_CODE = "toolactions.confirm.handler.not_pending";
const NOT_ALWAYS_APPROVABLE_CODE = "toolactions.confirm.handler.not_always_approvable";

const isTerminalStatus = (s: ConfirmActionStatus | ToolActionServerStatus): boolean =>
  s === "executed" || s === "rejected" || s === "failed" || s === "blocked";

export const toCardStatus = (s: ConfirmActionStatus | ToolActionServerStatus): CardStatus => {
  switch (s) {
    case "queued":
    case "executing":
      return "running";
    case "executed":
      return "executed";
    case "rejected":
      return "rejected";
    case "failed":
      return "failed";
    case "blocked":
      return "handled";
    default:
      return "pending";
  }
};

const hasNotifyToggle = (p: ConfirmActionPayload): p is CalendarAction | RsvpAction | MoveAction =>
  !isGmailAction(p) &&
  !isSlackAction(p) &&
  !isNotionAction(p) &&
  !isGDriveAction(p) &&
  !isLinearAction(p) &&
  !isGitHubAction(p) &&
  !isMCPAction(p) &&
  !isChiefAction(p);

// Approve/reject UI for a proposed email send or calendar write. The side
// effect runs server-side only on approval; the model is never in the send
// path. Subject, body, and title are model-generated and rendered as text only.
export const ConfirmActionRenderer = (props: ConfirmActionRendererProps) => {
  const rt = useChatRuntime();
  const t = rt.t;
  const { confirm, isSubmitting, error } = useToolActionConfirm();
  const chatActions = useChatToolActions(
    () => props.chatId,
    () => true
  );
  // A tool's own write links through that tool's check; one made through an
  // MCP server links only to the tool's own https host.
  const linkFor = (
    p: ConfirmActionPayload,
    isTool: (p: ConfirmActionPayload) => boolean,
    safe: (url: string | undefined) => string | undefined,
    hosts: readonly string[]
  ): string | undefined => {
    const url = chatActions.urlOf(p.actionId);
    return isTool(p) ? safe(url) : isMCPAction(p) ? mcpUrlOn(url, hosts) : undefined;
  };

  const [localStatus, setLocalStatus] = createSignal<CardStatus | null>(null);
  const [notifyAttendees, setNotifyAttendees] = createSignal<boolean | null>(null);
  const [editing, setEditing] = createSignal(false);

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<ConfirmActionPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseConfirmActionPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  // Precedence: a terminal payload status, then a terminal looked-up status,
  // then this session's click, then a non-terminal lookup. Approval only
  // queues the job, so the click shows the card as running and the lookup
  // that follows settles the real outcome over it. A frozen queued/executing
  // payload is only the last resort: auto-approved actions freeze "queued"
  // into the transcript before the async execute runs, so trusting it over the
  // lookup would show a later outcome as running forever.
  const status = (): CardStatus => {
    const p = payload();
    if (p && isTerminalStatus(p.status)) return toCardStatus(p.status);
    const fetched = chatActions.statusOf(p?.actionId);
    if (fetched && isTerminalStatus(fetched)) return toCardStatus(fetched);
    const local = localStatus();
    if (local) return local;
    if (fetched && fetched !== "pending") return toCardStatus(fetched);
    if (p && p.status !== "pending") return toCardStatus(p.status);
    return "pending";
  };

  const isPending = () => status() === "pending";

  // Hold the buttons until the lookup settles so a resolved action never flashes
  // a clickable Approve that would only 409.
  const resolving = () => chatActions.isLoading();

  // Only the person an action was proposed for can accept the ones that execute
  // inside their own mailbox or calendar; everyone else still gets Decline. An
  // undefined answer means the host doesn't report it, which leaves the card as
  // it was before the flag existed.
  const approvable = () => chatActions.approvableOf(payload()?.actionId) !== false;

  // Unlike `approvable`, an unknown answer hides the button: turning a
  // preference on is something the server has to have offered.
  const alwaysApproveOp = () => (approvable() ? chatActions.alwaysApproveOpOf(payload()?.actionId) : undefined);
  const [submittingAlways, setSubmittingAlways] = createSignal(false);
  // The preference this session's approve_always turned on, named once the action settles.
  const [savedAlwaysOp, setSavedAlwaysOp] = createSignal<string>();
  const mcpServerName = () => {
    const p = payload();
    return p && isMCPAction(p) ? p.preview.serverName : undefined;
  };

  const permissionNote = (): string | undefined => {
    const op = savedAlwaysOp();
    if (op) return alwaysAllowLabels(op, mcpServerName(), t).saved;
    if (status() !== "pending" && chatActions.autoApprovedOf(payload()?.actionId)) {
      return t("confirm_action_card.ran_automatically");
    }
    return undefined;
  };

  // The demotion to a compact receipt row is decided once, from the status at
  // the first settled read: a card the user resolves in this session keeps its
  // full layout instead of snapping to compact under them. The user can still
  // toggle either way afterwards.
  const [compact, setCompact] = createSignal<boolean | null>(null);
  createEffect(() => {
    if (props.surface !== "pinned" || compact() !== null) return;
    const p = payload();
    if (!p) return;
    if (p.status !== "pending") {
      setCompact(true);
      return;
    }
    if (chatActions.isSettled()) setCompact(status() !== "pending");
  });

  const isCompact = () => props.surface === "pinned" && (compact() ?? status() !== "pending");

  const linearPriorityLabel = (priority: number | undefined): string | undefined => {
    const key = priority === undefined ? undefined : priorityLabelKey(priority);
    return key ? t(key) : undefined;
  };

  const notifyChecked = (data: CalendarAction | RsvpAction | MoveAction): boolean =>
    notifyAttendees() ?? data.notifyAttendeesDefault;

  // Editing is only on the pinned card; the kinds excluded here carry nothing
  // the server lets the user change. A draft-send send_email is also read-only:
  // the backend sends the saved draft as-is, so edits wouldn't reach the wire.
  // Slack writes expose only their message text; a reaction has none.
  const isEditable = (p: ConfirmActionPayload): boolean => {
    if (props.surface === "timeline") return false;
    if (p.actionKind === "send_email") return !p.preview.draftId;
    if (isSlackAction(p)) return isSlackTextAction(p);
    // The server accepts no edited params for a Linear, GitHub or MCP write, so
    // an edit would be rejected on approve.
    if (isNotionAction(p) || isGDriveAction(p) || isLinearAction(p) || isGitHubAction(p) || isMCPAction(p)) {
      return false;
    }
    if (isChiefAction(p)) return isActionConfigAction(p);
    return (
      p.actionKind !== "calendar_delete" &&
      p.actionKind !== "calendar_rsvp" &&
      p.actionKind !== "calendar_move" &&
      p.actionKind !== "gmail_trash" &&
      p.actionKind !== "gmail_draft_update" &&
      p.actionKind !== "gmail_draft_delete"
    );
  };

  // A pending action is surfaced in the Action-required group; rendering it in the
  // timeline too would duplicate the card, so the timeline shows it only once it
  // resolves into history.
  const hideInTimeline = () => props.surface === "timeline" && isPending();

  const submit = async (decision: "approve" | "approve_always" | "reject", editedParams?: Record<string, unknown>) => {
    const p = payload();
    if (!p) return;
    // A click can land before the status lookup settles; pin the full layout
    // so the settle latch can't demote a card resolved by this session.
    if (compact() === null) setCompact(false);
    const notify = hasNotifyToggle(p) ? notifyChecked(p) : false;
    setSubmittingAlways(decision === "approve_always");
    // Read before the await: once the action leaves pending, a refetch reports
    // it no longer always-approvable and the op would be gone.
    const op = alwaysApproveOp();
    const result = await confirm(p.actionId, decision, notify, editedParams);
    if (result.ok) {
      // Approval only queues the side effect; the tracked approval polls for
      // the real outcome behind the running state.
      if (result.value === "rejected") {
        setLocalStatus("rejected");
        return;
      }
      setLocalStatus("running");
      if (decision === "approve_always") setSavedAlwaysOp(op);
      chatActions.trackApproval(p.actionId);
      return;
    }
    // A 409 means the action already left pending (handled in another tab, or a
    // stale card reopened after reload). Resolve the card so a retry can't
    // repeat the conflict. Hosts whose bindings reject with plain strings
    // (chief) carry the code inside the message, so scan that too.
    const info = extractErrorInfo(result.error);
    if (info.code === CONFLICT_CODE || info.message?.includes(CONFLICT_CODE)) setLocalStatus("handled");
    // The server no longer offers approve_always for this action. It is still
    // pending, so refetching drops the button and leaves Approve.
    if (info.code === NOT_ALWAYS_APPROVABLE_CODE || info.message?.includes(NOT_ALWAYS_APPROVABLE_CODE)) {
      chatActions.refresh();
    }
  };

  const errorMessage = (): string => {
    const info = extractErrorInfo(error());
    if (typeof info.code === "string") {
      const translated = t(info.code);
      // getTranslation returns the key itself when no entry matches.
      if (translated && translated !== info.code) return translated;
    }
    return info.message ?? t("confirm_action_card.failed");
  };

  // Only this session sees the confirm error; a card reopened after a reload, or
  // auto-approved with nobody watching, has just the reason the job stored.
  const failureMessage = (): string => {
    if (error()) return errorMessage();
    return chatActions.errorOf(payload()?.actionId) ?? t("confirm_action_card.failed");
  };

  const reconnectServer = (): MCPCallAction["preview"] | undefined => {
    const p = payload();
    if (!p || !isMCPAction(p) || status() !== "failed" || !chatActions.reconnectOf(p.actionId)) return undefined;
    return p.preview;
  };

  const settledAt = (): string => {
    const iso = chatActions.executedAtOf(payload()?.actionId);
    return iso ? formatIsoTimestamp(iso) : "";
  };

  const executedMessage = (data: ConfirmActionPayload): string => {
    if (data.actionKind === "send_email") return t("confirm_action_card.executed_send_email");
    if (data.actionKind === "gmail_trash") {
      return data.preview.untrash
        ? t("confirm_action_card.executed_gmail_untrash")
        : t("confirm_action_card.executed_gmail_trash");
    }
    if (data.actionKind === "gmail_draft_update") return t("confirm_action_card.executed_gmail_draft_update");
    if (data.actionKind === "gmail_draft_delete") return t("confirm_action_card.executed_gmail_draft_delete");
    if (data.actionKind === "send_message") return t("confirm_action_card.executed_slack_send");
    if (data.actionKind === "send_dm") return t("confirm_action_card.executed_slack_dm");
    if (data.actionKind === "reply_thread") return t("confirm_action_card.executed_slack_reply");
    if (data.actionKind === "add_reaction") return t("confirm_action_card.executed_slack_react");
    if (data.actionKind === "notion_create_page") return t("confirm_action_card.executed_notion_create_page");
    if (data.actionKind === "notion_update_page") return t("confirm_action_card.executed_notion_update_page");
    if (data.actionKind === "notion_create_db_row") return t("confirm_action_card.executed_notion_create_db_row");
    if (data.actionKind === "notion_update_db_row") return t("confirm_action_card.executed_notion_update_db_row");
    if (data.actionKind === "notion_create_database") return t("confirm_action_card.executed_notion_create_database");
    if (data.actionKind === "notion_add_comment") return t("confirm_action_card.executed_notion_add_comment");
    if (data.actionKind === "notion_update_database") return t("confirm_action_card.executed_notion_update_database");
    if (data.actionKind === "notion_move_page") return t("confirm_action_card.executed_notion_move_page");
    if (data.actionKind === "gdrive_create_doc") return t("confirm_action_card.executed_gdrive_create_doc");
    if (data.actionKind === "gdrive_update_doc") return t("confirm_action_card.executed_gdrive_update_doc");
    if (data.actionKind === "linear_create_issue") return t("confirm_action_card.executed_linear_create_issue");
    if (data.actionKind === "linear_update_issue") return t("confirm_action_card.executed_linear_update_issue");
    if (data.actionKind === "linear_add_comment") return t("confirm_action_card.executed_linear_add_comment");
    if (data.actionKind === "github_create_issue") return t("confirm_action_card.executed_github_create_issue");
    if (data.actionKind === "github_update_issue") return t("confirm_action_card.executed_github_update_issue");
    if (data.actionKind === "github_add_comment") return t("confirm_action_card.executed_github_add_comment");
    if (isMCPAction(data)) return t("confirm_action_card.mcp_call.executed");
    if (data.actionKind === "action_create") return t("confirm_action_card.executed_action_create");
    if (data.actionKind === "action_update") return t("confirm_action_card.executed_action_update");
    if (data.actionKind === "action_set_enabled") {
      return data.preview.enabled
        ? t("confirm_action_card.executed_action_enable")
        : t("confirm_action_card.executed_action_disable");
    }
    if (data.actionKind === "action_delete") return t("confirm_action_card.executed_action_delete");
    if (data.actionKind === "action_dry_run") return t("confirm_action_card.executed_action_dry_run");
    if (data.actionKind === "label_create") return t("confirm_action_card.executed_label_create");
    if (data.actionKind === "smart_tag_create") return t("confirm_action_card.executed_smart_tag_create");
    if (data.actionKind === "label_assign") return t("confirm_action_card.executed_label_assign");
    if (data.actionKind === "label_unassign") return t("confirm_action_card.executed_label_unassign");
    if (data.actionKind === "label_delete") return t("confirm_action_card.executed_label_delete");
    if (data.actionKind === "asset_update") return t("confirm_action_card.executed_asset_update");
    if (data.actionKind === "asset_delete") return t("confirm_action_card.executed_asset_delete");
    if (data.actionKind === "calendar_move") return t("confirm_action_card.executed_calendar_move");
    return t("confirm_action_card.executed_calendar");
  };

  const calendarWhen = (data: CalendarAction["preview"]): string => {
    if (!data.start) return "";
    if (data.allDay) return `${formatDay(data.start)} · ${t("calendar_card.all_day")}`;
    const day = formatDay(data.start);
    const startTime = formatTime(data.start);
    if (!data.end) return `${day} · ${startTime}`;
    const endTime = formatTime(data.end);
    if (isSameDay(data.start, data.end)) return `${day} · ${startTime}–${endTime}`;
    return `${day} ${startTime} – ${formatDay(data.end)} ${endTime}`;
  };

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {t("confirm_action_card.parse_error")}
        </div>
      }
    >
      <Show when={!hideInTimeline() && payload()}>
        {(data) => (
          <Show
            when={!isCompact()}
            fallback={
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCompact(false)}
                  aria-expanded="false"
                  class="group flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-md text-left focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
                >
                  <Switch fallback={<TbCalendarEvent class="size-4 shrink-0 text-muted-text" />}>
                    <Match when={data().actionKind === "send_email"}>
                      <TbMail class="size-4 shrink-0 text-muted-text" />
                    </Match>
                    <Match when={data().actionKind === "gmail_trash"}>
                      <TbTrash class="size-4 shrink-0 text-muted-text" />
                    </Match>
                    <Match when={data().actionKind === "gmail_draft_update"}>
                      <TbMail class="size-4 shrink-0 text-muted-text" />
                    </Match>
                    <Match when={data().actionKind === "gmail_draft_delete"}>
                      <TbTrash class="size-4 shrink-0 text-muted-text" />
                    </Match>
                    <Match when={isSlackAction(data())}>
                      <TbBrandSlack class="size-4 shrink-0 text-muted-text" />
                    </Match>
                    <Match when={isNotionAction(data())}>
                      <NotionIcon class="size-4 shrink-0" />
                    </Match>
                    <Match when={isLinearAction(data())}>
                      <LinearIcon class="size-4 shrink-0 text-muted-text" />
                    </Match>
                    <Match when={isGitHubAction(data())}>
                      <TbBrandGithub class="size-4 shrink-0 text-muted-text" />
                    </Match>
                    <Match when={isMCPAction(data())}>
                      <TbPlug class="size-4 shrink-0 text-muted-text" />
                    </Match>
                    <Match when={isGDriveAction(data())}>
                      <DriveIcon class="size-4 shrink-0" />
                    </Match>
                    <Match when={isChiefActionsAction(data())}>
                      <TbBolt class="size-4 shrink-0 text-muted-text" />
                    </Match>
                    <Match when={isLabelKind(data())}>
                      <TbTag class="size-4 shrink-0 text-muted-text" />
                    </Match>
                    <Match when={isAssetKind(data())}>
                      <TbFile class="size-4 shrink-0 text-muted-text" />
                    </Match>
                  </Switch>
                  <span class="truncate text-sm text-muted-text transition-colors group-hover:text-primary-text">
                    {t(titleKey(data()))}
                  </span>
                  <Switch>
                    <Match when={status() === "running"}>
                      <span class="inline-flex max-w-full min-w-0 items-center gap-1 text-xs text-muted-text">
                        <TbLoader2 class="size-3.5 shrink-0 animate-spin" />
                        <span class="truncate">{t("confirm_action_card.running")}</span>
                      </span>
                    </Match>
                    <Match when={status() === "executed"}>
                      <span class="inline-flex max-w-full min-w-0 items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                        <TbCheck class="size-3.5 shrink-0" />
                        <span class="truncate">{executedMessage(data())}</span>
                        <Show when={settledAt()}>
                          {(at) => <span class="shrink-0 font-normal text-muted-text">{at()}</span>}
                        </Show>
                      </span>
                    </Match>
                    <Match when={status() === "rejected"}>
                      <span class="inline-flex max-w-full min-w-0 items-center gap-1 text-xs text-muted-text">
                        <TbX class="size-3.5 shrink-0" />
                        <span class="truncate">{t("confirm_action_card.rejected")}</span>
                      </span>
                    </Match>
                    <Match when={status() === "failed"}>
                      <span class="inline-flex max-w-full min-w-0 items-center gap-1 text-xs text-red-500">
                        <TbAlertTriangle class="size-3.5 shrink-0" />
                        <span class="truncate">{failureMessage()}</span>
                        <Show when={settledAt()}>{(at) => <span class="shrink-0 text-muted-text">{at()}</span>}</Show>
                      </span>
                    </Match>
                    <Match when={status() === "handled"}>
                      <span class="inline-flex max-w-full min-w-0 items-center gap-1 text-xs text-muted-text">
                        <TbCheck class="size-3.5 shrink-0" />
                        <span class="truncate">{t("confirm_action_card.already_handled")}</span>
                      </span>
                    </Match>
                  </Switch>
                  <TbChevronRight class="size-3 shrink-0 text-muted-text" />
                </button>
                <Show when={reconnectServer()}>
                  {(server) => <McpReconnectButton serverId={server().serverId} serverName={server().serverName} />}
                </Show>
                <Show when={linkFor(data(), isNotionAction, safeNotionUrl, NOTION_HOSTS)}>
                  {(href) => (
                    <a
                      href={href()}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-brand hover:underline"
                    >
                      <NotionIcon class="size-3.5 shrink-0" />
                      {t("notion_database_card.open_link")}
                      <TbExternalLink class="size-3 shrink-0" aria-hidden="true" />
                    </a>
                  )}
                </Show>
                <Show when={linkFor(data(), isLinearAction, safeLinearUrl, LINEAR_HOSTS)}>
                  {(href) => (
                    <a
                      href={href()}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-brand hover:underline"
                    >
                      <LinearIcon class="size-3.5 shrink-0" />
                      {t("linear_comments_card.open_link")}
                      <TbExternalLink class="size-3 shrink-0" aria-hidden="true" />
                    </a>
                  )}
                </Show>
                <Show when={linkFor(data(), isGitHubAction, safeGitHubUrl, GITHUB_HOSTS)}>
                  {(href) => (
                    <a
                      href={href()}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-brand hover:underline"
                    >
                      <TbBrandGithub class="size-3.5 shrink-0" />
                      {t("github_issue_card.open_link")}
                      <TbExternalLink class="size-3 shrink-0" aria-hidden="true" />
                    </a>
                  )}
                </Show>
                <Show
                  when={
                    data().actionKind === "action_dry_run" &&
                    status() === "executed" &&
                    safeChatUrl(chatActions.urlOf(data().actionId))
                  }
                >
                  {(href) => (
                    <a
                      href={href()}
                      class="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-brand hover:underline"
                    >
                      {t("confirm_action_card.dry_run_view_link")}
                      <TbExternalLink class="size-3 shrink-0" aria-hidden="true" />
                    </a>
                  )}
                </Show>
                <Show
                  when={
                    isActionConfigAction(data()) &&
                    status() === "executed" &&
                    safeChatUrl(chatActions.urlOf(data().actionId))
                  }
                >
                  {(href) => <ActionViewEditLinks url={href()} />}
                </Show>
                <Show when={permissionNote()}>{(note) => <PermissionNote text={note()} />}</Show>
              </div>
            }
          >
            <div
              class={twMerge(
                "@container max-w-xl rounded-lg border border-primary-border bg-panel p-3",
                props.flat && "max-w-none rounded-none border-0 bg-transparent p-0"
              )}
            >
              <div class="flex items-start gap-3">
                <Switch fallback={<TbCalendarEvent class="size-5 shrink-0 text-brand" />}>
                  <Match when={data().actionKind === "send_email"}>
                    <TbMail class="size-5 shrink-0 text-brand" />
                  </Match>
                  <Match when={data().actionKind === "gmail_trash"}>
                    <TbTrash class="size-5 shrink-0 text-brand" />
                  </Match>
                  <Match when={data().actionKind === "gmail_draft_update"}>
                    <TbMail class="size-5 shrink-0 text-brand" />
                  </Match>
                  <Match when={data().actionKind === "gmail_draft_delete"}>
                    <TbTrash class="size-5 shrink-0 text-brand" />
                  </Match>
                  <Match when={isSlackAction(data())}>
                    <TbBrandSlack class="size-5 shrink-0 text-brand" />
                  </Match>
                  <Match when={isNotionAction(data())}>
                    <NotionIcon class="size-5 shrink-0" />
                  </Match>
                  <Match when={isLinearAction(data())}>
                    <LinearIcon class="size-5 shrink-0 text-brand" />
                  </Match>
                  <Match when={isGitHubAction(data())}>
                    <TbBrandGithub class="size-5 shrink-0 text-brand" />
                  </Match>
                  <Match when={isMCPAction(data())}>
                    <TbPlug class="size-5 shrink-0 text-brand" />
                  </Match>
                  <Match when={isGDriveAction(data())}>
                    <DriveIcon class="size-5 shrink-0" />
                  </Match>
                  <Match when={isChiefActionsAction(data())}>
                    <TbBolt class="size-5 shrink-0 text-brand" />
                  </Match>
                  <Match when={isLabelKind(data())}>
                    <TbTag class="size-5 shrink-0 text-brand" />
                  </Match>
                  <Match when={isAssetKind(data())}>
                    <TbFile class="size-5 shrink-0 text-brand" />
                  </Match>
                </Switch>
                <div class="min-w-0 flex-1">
                  <div class="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                    <h3 class="text-sm font-medium text-primary-text">{t(titleKey(data()))}</h3>
                    <div class="flex flex-wrap items-center gap-2">
                      <Show when={linkFor(data(), isNotionAction, safeNotionUrl, NOTION_HOSTS)}>
                        {(href) => (
                          <a
                            href={href()}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-brand hover:underline"
                          >
                            <NotionIcon class="size-3.5 shrink-0" />
                            {t("notion_database_card.open_link")}
                            <TbExternalLink class="size-3 shrink-0" aria-hidden="true" />
                          </a>
                        )}
                      </Show>
                      <Show when={linkFor(data(), isLinearAction, safeLinearUrl, LINEAR_HOSTS)}>
                        {(href) => (
                          <a
                            href={href()}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-brand hover:underline"
                          >
                            <LinearIcon class="size-3.5 shrink-0" />
                            {t("linear_comments_card.open_link")}
                            <TbExternalLink class="size-3 shrink-0" aria-hidden="true" />
                          </a>
                        )}
                      </Show>
                      <Show when={linkFor(data(), isGitHubAction, safeGitHubUrl, GITHUB_HOSTS)}>
                        {(href) => (
                          <a
                            href={href()}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-brand hover:underline"
                          >
                            <TbBrandGithub class="size-3.5 shrink-0" />
                            {t("github_issue_card.open_link")}
                            <TbExternalLink class="size-3 shrink-0" aria-hidden="true" />
                          </a>
                        )}
                      </Show>
                      <Show
                        when={
                          data().actionKind === "action_dry_run" &&
                          status() === "executed" &&
                          safeChatUrl(chatActions.urlOf(data().actionId))
                        }
                      >
                        {(href) => (
                          <a
                            href={href()}
                            class="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-brand hover:underline"
                          >
                            {t("confirm_action_card.dry_run_view_link")}
                            <TbExternalLink class="size-3 shrink-0" aria-hidden="true" />
                          </a>
                        )}
                      </Show>
                      <Show
                        when={
                          isActionConfigAction(data()) &&
                          status() === "executed" &&
                          safeChatUrl(chatActions.urlOf(data().actionId))
                        }
                      >
                        {(href) => <ActionViewEditLinks url={href()} />}
                      </Show>
                      <Show when={props.surface === "pinned" && !isPending()}>
                        <button
                          type="button"
                          onClick={() => setCompact(true)}
                          aria-expanded="true"
                          aria-label={t("action.collapse")}
                          class="shrink-0 cursor-pointer rounded-md text-muted-text transition-colors hover:text-primary-text focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
                        >
                          <TbChevronRight class="size-3.5 rotate-90" />
                        </button>
                      </Show>
                    </div>
                  </div>

                  <Show when={!editing()}>
                    <div class="mt-2 flex flex-col gap-3 @max-md:*:self-stretch @md:flex-row @md:items-stretch @md:justify-between">
                      <Switch>
                        <Match when={data().actionKind === "send_email" && (data() as SendEmailAction)}>
                          {(email) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <Show when={email().preview.draftId}>
                                <p class="text-xs font-medium text-muted-text">
                                  {t("confirm_action_card.sending_draft")}
                                </p>
                              </Show>
                              <p class="truncate text-xs text-muted-text">
                                {t("confirm_action_card.to_label")} {email().preview.to.join(", ")}
                              </p>
                              <Show when={nonEmpty(email().preview.cc)}>
                                {(cc) => (
                                  <p class="truncate text-xs text-muted-text">
                                    {t("confirm_action_card.cc_label")} {cc().join(", ")}
                                  </p>
                                )}
                              </Show>
                              <p class="truncate text-sm font-bold text-primary-text">
                                <span class="font-normal text-muted-text">
                                  {t("confirm_action_card.subject_label")}{" "}
                                </span>
                                {email().preview.subject}
                              </p>
                              <p class="mt-1 line-clamp-6 text-xs wrap-break-word whitespace-pre-wrap text-muted-text">
                                {email().preview.body}
                              </p>
                            </div>
                          )}
                        </Match>

                        <Match when={data().actionKind === "calendar_rsvp" && (data() as RsvpAction)}>
                          {(rsvp) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="text-sm text-primary-text">
                                <span class="text-muted-text">{t("confirm_action_card.rsvp_response_label")} </span>
                                <span class="font-bold">{t(RSVP_RESPONSE_KEY[rsvp().preview.response])}</span>
                              </p>
                            </div>
                          )}
                        </Match>

                        <Match when={data().actionKind === "gmail_trash" && (data() as GmailTrashAction)}>
                          {(trash) => (
                            <div class="min-w-0 flex-1 space-y-2 self-start">
                              <p class="text-sm text-primary-text">
                                {trash().preview.count === 1
                                  ? t(
                                      trash().preview.untrash
                                        ? "confirm_action_card.gmail_untrash_summary_one"
                                        : "confirm_action_card.gmail_trash_summary_one"
                                    )
                                  : t(
                                      trash().preview.untrash
                                        ? "confirm_action_card.gmail_untrash_summary"
                                        : "confirm_action_card.gmail_trash_summary",
                                      { count: String(trash().preview.count) }
                                    )}
                              </p>
                              <Show when={trash().preview.messages.length > 0}>
                                <ul class="max-h-64 divide-y divide-primary-border overflow-y-auto rounded-md border border-primary-border">
                                  <For each={trash().preview.messages}>
                                    {(message) => (
                                      <li class="px-2.5 py-1.5">
                                        <div class="flex items-baseline justify-between gap-3">
                                          <span class="truncate text-xs text-muted-text">
                                            {senderDisplayName(message.from)}
                                          </span>
                                          <Show when={message.date}>
                                            <span class="shrink-0 text-xs text-muted-text">
                                              {formatIsoTimestamp(message.date)}
                                            </span>
                                          </Show>
                                        </div>
                                        <p class="truncate text-sm text-primary-text">{message.subject}</p>
                                      </li>
                                    )}
                                  </For>
                                </ul>
                                <Show when={trash().preview.count > trash().preview.messages.length}>
                                  <p class="text-xs text-muted-text">
                                    {t("confirm_action_card.gmail_trash_more", {
                                      count: String(trash().preview.count - trash().preview.messages.length),
                                    })}
                                  </p>
                                </Show>
                              </Show>
                            </div>
                          )}
                        </Match>

                        <Match when={data().actionKind === "gmail_draft_update" && (data() as GmailDraftUpdateAction)}>
                          {(draft) => (
                            <div class="min-w-0 flex-1 space-y-2 self-start">
                              <div class="space-y-1">
                                <p class="text-xs font-medium text-muted-text">
                                  {t("confirm_action_card.draft_current_label")}
                                </p>
                                <p class="truncate text-xs text-muted-text">
                                  {t("confirm_action_card.to_label")} {draft().preview.currentTo.join(", ")}
                                </p>
                                <p class="truncate text-sm text-muted-text line-through">
                                  <span class="font-normal">{t("confirm_action_card.subject_label")} </span>
                                  {draft().preview.currentSubject || t("confirm_action_card.draft_no_subject")}
                                </p>
                              </div>
                              <div class="space-y-1">
                                <p class="inline-flex items-center gap-1 text-xs font-medium text-muted-text">
                                  <TbArrowRight class="size-3.5 shrink-0" />
                                  {t("confirm_action_card.draft_new_label")}
                                </p>
                                <p class="truncate text-xs text-muted-text">
                                  {t("confirm_action_card.to_label")} {draft().preview.to.join(", ")}
                                </p>
                                <Show when={draft().preview.cc.length > 0}>
                                  <p class="truncate text-xs text-muted-text">
                                    {t("confirm_action_card.cc_label")} {draft().preview.cc.join(", ")}
                                  </p>
                                </Show>
                                <Show when={draft().preview.bcc.length > 0}>
                                  <p class="truncate text-xs text-muted-text">
                                    {t("confirm_action_card.bcc_label")} {draft().preview.bcc.join(", ")}
                                  </p>
                                </Show>
                                <p class="truncate text-sm font-bold text-primary-text">
                                  <span class="font-normal text-muted-text">
                                    {t("confirm_action_card.subject_label")}{" "}
                                  </span>
                                  {draft().preview.subject || t("confirm_action_card.draft_no_subject")}
                                </p>
                                <p class="mt-1 line-clamp-6 text-xs wrap-break-word whitespace-pre-wrap text-muted-text">
                                  {draft().preview.body}
                                </p>
                              </div>
                            </div>
                          )}
                        </Match>

                        <Match when={data().actionKind === "gmail_draft_delete" && (data() as GmailDraftDeleteAction)}>
                          {(draft) => (
                            <div class="min-w-0 flex-1 space-y-2 self-start">
                              <p class="inline-flex items-center gap-1 text-xs text-red-500">
                                <TbAlertTriangle class="size-3.5 shrink-0" />
                                {t("confirm_action_card.draft_delete_warning")}
                              </p>
                              <div class="space-y-1">
                                <p class="truncate text-xs text-muted-text">
                                  {t("confirm_action_card.to_label")} {draft().preview.currentTo.join(", ")}
                                </p>
                                <p class="truncate text-sm font-bold text-primary-text line-through">
                                  <span class="font-normal text-muted-text">
                                    {t("confirm_action_card.subject_label")}{" "}
                                  </span>
                                  {draft().preview.currentSubject || t("confirm_action_card.draft_no_subject")}
                                </p>
                              </div>
                            </div>
                          )}
                        </Match>

                        <Match when={data().actionKind === "calendar_move" && (data() as MoveAction)}>
                          {(move) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="text-xs text-muted-text">
                                {t("confirm_action_card.move_from_label")}{" "}
                                <span class="text-primary-text">
                                  {move().preview.calendarId || t("confirm_action_card.calendar_primary")}
                                </span>
                              </p>
                              <p class="inline-flex items-center gap-1 text-xs text-muted-text">
                                <TbArrowRight class="size-3.5 shrink-0" />
                                {t("confirm_action_card.move_to_label")}{" "}
                                <span class="text-primary-text">{move().preview.destinationCalendarId}</span>
                              </p>
                            </div>
                          )}
                        </Match>

                        <Match
                          when={
                            (data().actionKind === "send_message" || data().actionKind === "reply_thread") &&
                            (data() as SlackSendMessageAction | SlackReplyAction)
                          }
                        >
                          {(slack) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-xs text-muted-text">
                                {t("confirm_action_card.slack_channel_label")}{" "}
                                <span class="text-primary-text">#{slack().preview.channelName}</span>
                              </p>
                              <Show when={slack().actionKind === "reply_thread"}>
                                <p class="text-xs text-muted-text">{t("confirm_action_card.slack_thread_note")}</p>
                              </Show>
                              <p class="mt-1 line-clamp-6 text-sm wrap-break-word whitespace-pre-wrap text-primary-text">
                                <SlackMessageText text={slack().preview.text} mentions={slack().preview.mentions} />
                              </p>
                              <p class="text-xs text-muted-text">{t("confirm_action_card.slack_posts_as_chief")}</p>
                            </div>
                          )}
                        </Match>

                        <Match when={data().actionKind === "send_dm" && (data() as SlackSendDmAction)}>
                          {(dm) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-xs text-muted-text">
                                {t("confirm_action_card.to_label")}{" "}
                                <span class="text-primary-text">{dm().preview.recipientName}</span>
                              </p>
                              <p class="mt-1 line-clamp-6 text-sm wrap-break-word whitespace-pre-wrap text-primary-text">
                                <SlackMessageText text={dm().preview.text} mentions={dm().preview.mentions} />
                              </p>
                              <p class="text-xs text-muted-text">{t("confirm_action_card.slack_posts_as_chief")}</p>
                            </div>
                          )}
                        </Match>

                        <Match when={data().actionKind === "add_reaction" && (data() as SlackReactionAction)}>
                          {(reaction) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="text-sm text-primary-text">
                                <span class="text-muted-text">{t("confirm_action_card.slack_emoji_label")} </span>
                                <span class="font-bold">:{reaction().preview.emoji}:</span>
                              </p>
                              <p class="truncate text-xs text-muted-text">
                                {t("confirm_action_card.slack_channel_label")}{" "}
                                <span class="text-primary-text">#{reaction().preview.channelName}</span>
                              </p>
                              <Show when={reaction().preview.messageText}>
                                <p class="line-clamp-2 text-xs wrap-break-word whitespace-pre-wrap text-muted-text">
                                  {reaction().preview.messageText}
                                </p>
                              </Show>
                            </div>
                          )}
                        </Match>

                        <Match when={data().actionKind === "notion_create_page" && (data() as NotionCreatePageAction)}>
                          {(create) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <Show when={create().preview.parentDatabaseId}>
                                <p class="truncate text-xs text-muted-text">
                                  {t("confirm_action_card.notion_in_database")}{" "}
                                  <Show
                                    when={create().preview.parentTitle}
                                    fallback={
                                      <span class="font-mono text-primary-text">
                                        {create().preview.parentDatabaseId}
                                      </span>
                                    }
                                  >
                                    {(title) => <span class="text-primary-text">{title()}</span>}
                                  </Show>
                                </p>
                              </Show>
                              <Show when={create().preview.parentPageId}>
                                <p class="truncate text-xs text-muted-text">
                                  {t("confirm_action_card.notion_under_page")}{" "}
                                  <Show
                                    when={create().preview.parentTitle}
                                    fallback={
                                      <span class="font-mono text-primary-text">{create().preview.parentPageId}</span>
                                    }
                                  >
                                    {(title) => <span class="text-primary-text">{title()}</span>}
                                  </Show>
                                </p>
                              </Show>
                              <Show when={create().preview.title || create().preview.iconEmoji}>
                                <p class="truncate text-sm font-bold text-primary-text">
                                  <Show when={create().preview.iconEmoji}>{(emoji) => <span>{emoji()} </span>}</Show>
                                  {create().preview.title}
                                </p>
                              </Show>
                              <Show when={create().preview.markdown}>
                                {(markdown) => <NotionMarkdownPreview text={markdown()} />}
                              </Show>
                              <Show when={notionBlocksText(create().preview.blocks)}>
                                {(text) => (
                                  <p class="mt-1 line-clamp-6 text-xs wrap-break-word whitespace-pre-wrap text-muted-text">
                                    {text()}
                                  </p>
                                )}
                              </Show>
                              <Show when={nonEmpty(create().preview.properties)}>
                                {(properties) => <NotionProperties properties={properties()} />}
                              </Show>
                            </div>
                          )}
                        </Match>

                        <Match when={data().actionKind === "notion_update_page" && (data() as NotionUpdatePageAction)}>
                          {(update) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-xs text-muted-text">
                                {t("confirm_action_card.notion_page_label")}{" "}
                                <Show
                                  when={update().preview.pageTitle}
                                  fallback={<span class="font-mono text-primary-text">{update().preview.pageId}</span>}
                                >
                                  {(title) => <span class="text-primary-text">{title()}</span>}
                                </Show>
                              </p>
                              <Show when={update().preview.archived === true}>
                                <p class="text-sm text-primary-text">{t("confirm_action_card.notion_archive")}</p>
                              </Show>
                              <Show when={update().preview.archived === false}>
                                <p class="text-sm text-primary-text">{t("confirm_action_card.notion_restore")}</p>
                              </Show>
                              <Show when={nonEmpty(update().preview.edits)}>
                                {(edits) => <NotionEdits edits={edits()} />}
                              </Show>
                              <Show when={update().preview.replaceMarkdown !== undefined}>
                                <Show
                                  when={update().preview.replaceMarkdown}
                                  fallback={
                                    <p class="text-xs font-medium text-primary-text">
                                      {t("confirm_action_card.notion_clear_content")}
                                    </p>
                                  }
                                >
                                  {(markdown) => (
                                    <>
                                      <p class="text-xs font-medium text-primary-text">
                                        {t("confirm_action_card.notion_replace_content")}
                                      </p>
                                      <p class="mt-1 line-clamp-6 text-xs wrap-break-word whitespace-pre-wrap text-muted-text">
                                        {markdown()}
                                      </p>
                                    </>
                                  )}
                                </Show>
                              </Show>
                              <Show when={(update().preview.blocks?.length ?? 0) > 0}>
                                <Show
                                  when={update().preview.replaceContent === true}
                                  fallback={
                                    <p class="text-xs font-medium text-primary-text">
                                      {t("confirm_action_card.notion_append_content")}
                                    </p>
                                  }
                                >
                                  <p class="text-xs font-medium text-primary-text">
                                    {t("confirm_action_card.notion_replace_content")}
                                  </p>
                                </Show>
                              </Show>
                              <Show when={notionBlocksText(update().preview.blocks)}>
                                {(text) => (
                                  <p class="mt-1 line-clamp-6 text-xs wrap-break-word whitespace-pre-wrap text-muted-text">
                                    {text()}
                                  </p>
                                )}
                              </Show>
                              <Show when={update().preview.position === "start"}>
                                <p class="text-xs text-muted-text">{t("confirm_action_card.notion_position_start")}</p>
                              </Show>
                              <Show when={update().preview.position === "end"}>
                                <p class="text-xs text-muted-text">{t("confirm_action_card.notion_position_end")}</p>
                              </Show>
                              <Show when={update().preview.afterBlockId}>
                                {(blockId) => (
                                  <p class="truncate text-xs text-muted-text">
                                    {t("confirm_action_card.notion_after_block_label")}{" "}
                                    <span class="font-mono text-primary-text">{blockId()}</span>
                                  </p>
                                )}
                              </Show>
                              <Show when={update().preview.allowDeletingContent}>
                                <p class="inline-flex items-center gap-1 text-xs text-red-500">
                                  <TbAlertTriangle class="size-3.5 shrink-0" />
                                  {t("confirm_action_card.notion_allow_deleting_warning")}
                                </p>
                              </Show>
                            </div>
                          )}
                        </Match>

                        <Match
                          when={data().actionKind === "notion_create_db_row" && (data() as NotionCreateDbRowAction)}
                        >
                          {(row) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-xs text-muted-text">
                                {t("confirm_action_card.notion_database_label")}{" "}
                                <Show
                                  when={row().preview.databaseTitle}
                                  fallback={<span class="font-mono text-primary-text">{row().preview.databaseId}</span>}
                                >
                                  {(title) => <span class="text-primary-text">{title()}</span>}
                                </Show>
                              </p>
                              <NotionProperties properties={row().preview.properties} />
                            </div>
                          )}
                        </Match>

                        <Match
                          when={data().actionKind === "notion_update_db_row" && (data() as NotionUpdateDbRowAction)}
                        >
                          {(row) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-xs text-muted-text">
                                {t("confirm_action_card.notion_row_label")}{" "}
                                <Show
                                  when={row().preview.rowTitle}
                                  fallback={<span class="font-mono text-primary-text">{row().preview.pageId}</span>}
                                >
                                  {(title) => <span class="text-primary-text">{title()}</span>}
                                </Show>
                              </p>
                              <NotionProperties properties={row().preview.properties} />
                            </div>
                          )}
                        </Match>

                        <Match
                          when={
                            data().actionKind === "notion_create_database" && (data() as NotionCreateDatabaseAction)
                          }
                        >
                          {(database) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-xs text-muted-text">
                                {t("confirm_action_card.notion_under_page")}{" "}
                                <Show
                                  when={database().preview.parentTitle}
                                  fallback={
                                    <span class="font-mono text-primary-text">{database().preview.parentPageId}</span>
                                  }
                                >
                                  {(title) => <span class="text-primary-text">{title()}</span>}
                                </Show>
                              </p>
                              <p class="truncate text-sm font-bold text-primary-text">{database().preview.title}</p>
                              <Show when={Object.keys(database().preview.properties).length > 0}>
                                <NotionColumns columns={database().preview.properties} />
                              </Show>
                              <Show when={nonEmpty(database().preview.rows)}>
                                {(rows) => <NotionRows rows={rows()} />}
                              </Show>
                            </div>
                          )}
                        </Match>

                        <Match when={data().actionKind === "notion_add_comment" && (data() as NotionAddCommentAction)}>
                          {(comment) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <Show when={comment().preview.pageId}>
                                <p class="truncate text-xs text-muted-text">
                                  {t("confirm_action_card.notion_page_label")}{" "}
                                  <Show
                                    when={comment().preview.pageTitle}
                                    fallback={
                                      <span class="font-mono text-primary-text">{comment().preview.pageId}</span>
                                    }
                                  >
                                    {(title) => <span class="text-primary-text">{title()}</span>}
                                  </Show>
                                </p>
                              </Show>
                              <Show when={comment().preview.discussionId}>
                                <p class="truncate text-xs text-muted-text">
                                  {t("confirm_action_card.notion_discussion_label")}{" "}
                                  <span class="font-mono text-primary-text">{comment().preview.discussionId}</span>
                                </p>
                              </Show>
                              <p class="mt-1 line-clamp-6 text-sm wrap-break-word whitespace-pre-wrap text-primary-text">
                                {comment().preview.text}
                              </p>
                            </div>
                          )}
                        </Match>

                        <Match
                          when={
                            data().actionKind === "notion_update_database" && (data() as NotionUpdateDatabaseAction)
                          }
                        >
                          {(database) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-xs text-muted-text">
                                {t("confirm_action_card.notion_database_label")}{" "}
                                <Show
                                  when={database().preview.databaseTitle}
                                  fallback={
                                    <span class="font-mono text-primary-text">{database().preview.databaseId}</span>
                                  }
                                >
                                  {(title) => <span class="text-primary-text">{title()}</span>}
                                </Show>
                              </p>
                              <Show when={database().preview.title}>
                                {(title) => (
                                  <p class="truncate text-xs text-muted-text">
                                    {t("confirm_action_card.notion_new_title_label")}{" "}
                                    <span class="font-bold text-primary-text">{title()}</span>
                                  </p>
                                )}
                              </Show>
                              <Show when={nonEmpty(database().preview.addColumns)}>
                                {(columns) => (
                                  <NotionColumns
                                    columns={columns()}
                                    labelKey="confirm_action_card.notion_add_columns_label"
                                  />
                                )}
                              </Show>
                              <Show when={nonEmpty(database().preview.removeColumns)}>
                                {(names) => (
                                  <p class="inline-flex max-w-full min-w-0 items-center gap-1 text-xs text-red-500">
                                    <TbAlertTriangle class="size-3.5 shrink-0" />
                                    {t("confirm_action_card.notion_remove_columns_label")}{" "}
                                    <span class="truncate">{names().join(", ")}</span>
                                  </p>
                                )}
                              </Show>
                            </div>
                          )}
                        </Match>

                        <Match when={data().actionKind === "notion_move_page" && (data() as NotionMovePageAction)}>
                          {(move) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-xs text-muted-text">
                                {t("confirm_action_card.notion_page_label")}{" "}
                                <Show
                                  when={move().preview.pageTitle}
                                  fallback={<span class="font-mono text-primary-text">{move().preview.pageId}</span>}
                                >
                                  {(title) => <span class="text-primary-text">{title()}</span>}
                                </Show>
                              </p>
                              <Show when={move().preview.newParentPageId}>
                                {(parentId) => (
                                  <p class="inline-flex max-w-full min-w-0 items-center gap-1 text-xs text-muted-text">
                                    <TbArrowRight class="size-3.5 shrink-0" />
                                    {t("confirm_action_card.notion_under_page")}{" "}
                                    <Show
                                      when={move().preview.parentTitle}
                                      fallback={<span class="truncate font-mono text-primary-text">{parentId()}</span>}
                                    >
                                      {(title) => <span class="truncate text-primary-text">{title()}</span>}
                                    </Show>
                                  </p>
                                )}
                              </Show>
                              <Show when={move().preview.newParentDataSourceId}>
                                {(dataSourceId) => (
                                  <p class="inline-flex max-w-full min-w-0 items-center gap-1 text-xs text-muted-text">
                                    <TbArrowRight class="size-3.5 shrink-0" />
                                    {t("confirm_action_card.notion_in_database")}{" "}
                                    <span class="truncate font-mono text-primary-text">{dataSourceId()}</span>
                                  </p>
                                )}
                              </Show>
                            </div>
                          )}
                        </Match>

                        <Match when={data().actionKind === "gdrive_create_doc" && (data() as GDriveCreateDocAction)}>
                          {(create) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-sm font-bold text-primary-text">{create().preview.title}</p>
                              <Show when={create().preview.parentFolderId}>
                                {(folderId) => (
                                  <p class="truncate text-xs text-muted-text">
                                    {t("confirm_action_card.gdrive_in_folder")}{" "}
                                    <span class="font-mono text-primary-text">{folderId()}</span>
                                  </p>
                                )}
                              </Show>
                              <Show when={create().preview.content}>
                                {(content) => (
                                  <p class="mt-1 line-clamp-6 text-xs wrap-break-word whitespace-pre-wrap text-muted-text">
                                    {content()}
                                  </p>
                                )}
                              </Show>
                            </div>
                          )}
                        </Match>

                        <Match when={data().actionKind === "gdrive_update_doc" && (data() as GDriveUpdateDocAction)}>
                          {(update) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-xs text-muted-text">
                                {t("confirm_action_card.gdrive_doc_label")}{" "}
                                <Show
                                  when={update().preview.fileTitle}
                                  fallback={<span class="font-mono text-primary-text">{update().preview.fileId}</span>}
                                >
                                  {(title) => <span class="text-primary-text">{title()}</span>}
                                </Show>
                              </p>
                              <p class="text-xs font-medium text-primary-text">
                                {update().preview.mode === "append"
                                  ? t("confirm_action_card.gdrive_append_content")
                                  : t("confirm_action_card.gdrive_replace_content")}
                              </p>
                              <Show when={update().preview.content}>
                                {(content) => (
                                  <p class="mt-1 line-clamp-6 text-xs wrap-break-word whitespace-pre-wrap text-muted-text">
                                    {content()}
                                  </p>
                                )}
                              </Show>
                            </div>
                          )}
                        </Match>

                        <Match
                          when={data().actionKind === "linear_create_issue" && (data() as LinearCreateIssueAction)}
                        >
                          {(create) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-sm font-bold text-primary-text">{create().preview.title}</p>
                              <p class="truncate text-xs text-muted-text">
                                {t("confirm_action_card.linear_team")}{" "}
                                <span class="text-primary-text">{create().preview.teamName}</span>
                              </p>
                              <div class="flex flex-wrap items-center gap-1.5 text-xs text-muted-text">
                                <Show when={create().preview.state}>
                                  {(state) => <span class="rounded bg-inset px-1.5 py-0.5">{state()}</span>}
                                </Show>
                                <Show when={linearPriorityLabel(create().preview.priority)}>
                                  {(label) => <span>{label()}</span>}
                                </Show>
                                <Show when={create().preview.assignee}>
                                  {(assignee) => <span class="truncate">{assignee()}</span>}
                                </Show>
                                <Show when={create().preview.project}>
                                  {(project) => <span class="truncate">{project()}</span>}
                                </Show>
                                <For each={create().preview.labels}>
                                  {(label) => <span class="truncate rounded bg-inset px-1.5 py-0.5">{label}</span>}
                                </For>
                              </div>
                              <Show when={create().preview.description}>
                                {(description) => (
                                  <p class="mt-1 line-clamp-6 text-xs wrap-break-word whitespace-pre-wrap text-muted-text">
                                    {description()}
                                  </p>
                                )}
                              </Show>
                            </div>
                          )}
                        </Match>

                        <Match
                          when={data().actionKind === "linear_update_issue" && (data() as LinearUpdateIssueAction)}
                        >
                          {(update) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-sm font-bold text-primary-text">
                                <span class="font-mono text-xs text-muted-text">{update().preview.identifier}</span>{" "}
                                {update().preview.issueTitle}
                              </p>
                              <ChangeLine label="confirm_action_card.linear_new_title" value={update().preview.title} />
                              <ChangeLine
                                label="confirm_action_card.linear_assignee"
                                value={update().preview.assignee}
                              />
                              <ChangeLine label="confirm_action_card.linear_state" value={update().preview.state} />
                              <ChangeLine
                                label="confirm_action_card.linear_priority"
                                value={linearPriorityLabel(update().preview.priority)}
                              />
                              <ChangeLine label="confirm_action_card.linear_project" value={update().preview.project} />
                              <ChangeLine
                                label="confirm_action_card.linear_labels_added"
                                value={update().preview.addedLabels?.join(", ")}
                              />
                              <ChangeLine
                                label="confirm_action_card.linear_labels_removed"
                                value={update().preview.removedLabels?.join(", ")}
                              />
                              <Show when={update().preview.description !== undefined}>
                                <p class="text-xs text-muted-text">
                                  {t("confirm_action_card.linear_description")}{" "}
                                  <Show
                                    when={update().preview.description}
                                    fallback={
                                      <span class="text-primary-text">
                                        {t("confirm_action_card.linear_description_cleared")}
                                      </span>
                                    }
                                  >
                                    {(description) => (
                                      <span class="mt-1 line-clamp-6 wrap-break-word whitespace-pre-wrap text-primary-text">
                                        {description()}
                                      </span>
                                    )}
                                  </Show>
                                </p>
                              </Show>
                            </div>
                          )}
                        </Match>

                        <Match when={data().actionKind === "linear_add_comment" && (data() as LinearAddCommentAction)}>
                          {(comment) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-xs text-muted-text">
                                {t("confirm_action_card.linear_issue_label")}{" "}
                                <span class="font-mono text-primary-text">{comment().preview.identifier}</span>{" "}
                                <span class="text-primary-text">{comment().preview.issueTitle}</span>
                              </p>
                              <p class="mt-1 line-clamp-6 text-xs wrap-break-word whitespace-pre-wrap text-muted-text">
                                {comment().preview.body}
                              </p>
                            </div>
                          )}
                        </Match>

                        <Match
                          when={data().actionKind === "github_create_issue" && (data() as GitHubCreateIssueAction)}
                        >
                          {(create) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-sm font-bold text-primary-text">{create().preview.title}</p>
                              <p class="truncate font-mono text-xs text-muted-text">{create().preview.repo}</p>
                              <div class="flex flex-wrap items-center gap-1.5 text-xs text-muted-text">
                                <For each={create().preview.assignees}>
                                  {(assignee) => <span class="truncate">{assignee}</span>}
                                </For>
                                <For each={create().preview.labels}>
                                  {(label) => <span class="truncate rounded bg-inset px-1.5 py-0.5">{label}</span>}
                                </For>
                              </div>
                              <Show when={create().preview.body}>
                                {(body) => (
                                  <p class="mt-1 line-clamp-6 text-xs wrap-break-word whitespace-pre-wrap text-muted-text">
                                    {body()}
                                  </p>
                                )}
                              </Show>
                            </div>
                          )}
                        </Match>

                        <Match
                          when={data().actionKind === "github_update_issue" && (data() as GitHubUpdateIssueAction)}
                        >
                          {(update) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-sm font-bold text-primary-text">
                                <span class="font-mono text-xs text-muted-text">{update().preview.reference}</span>{" "}
                                {update().preview.issueTitle}
                              </p>
                              <ChangeLine label="confirm_action_card.github_state" value={update().preview.state} />
                              <ChangeLine
                                label="confirm_action_card.github_state_reason"
                                value={update().preview.stateReason}
                              />
                              <ChangeLine
                                label="confirm_action_card.github_assignees_added"
                                value={update().preview.addAssignees?.join(", ")}
                              />
                              <ChangeLine
                                label="confirm_action_card.github_assignees_removed"
                                value={update().preview.removeAssignees?.join(", ")}
                              />
                              <ChangeLine
                                label="confirm_action_card.github_labels_added"
                                value={update().preview.addLabels?.join(", ")}
                              />
                              <ChangeLine
                                label="confirm_action_card.github_labels_removed"
                                value={update().preview.removeLabels?.join(", ")}
                              />
                            </div>
                          )}
                        </Match>

                        <Match when={data().actionKind === "github_add_comment" && (data() as GitHubAddCommentAction)}>
                          {(comment) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-xs text-muted-text">
                                {t("confirm_action_card.github_issue_label")}{" "}
                                <span class="font-mono text-primary-text">{comment().preview.reference}</span>{" "}
                                <span class="text-primary-text">{comment().preview.issueTitle}</span>
                              </p>
                              <p class="mt-1 line-clamp-6 text-xs wrap-break-word whitespace-pre-wrap text-muted-text">
                                {comment().preview.body}
                              </p>
                            </div>
                          )}
                        </Match>

                        <Match when={isMCPAction(data()) && (data() as MCPCallAction)}>
                          {(call) => (
                            <div class="min-w-0 flex-1 space-y-2 self-start">
                              <p class="truncate text-sm text-primary-text">
                                <span class="font-bold">{call().preview.serverName}</span>{" "}
                                <span class="font-mono text-xs text-muted-text">{call().preview.toolName}</span>
                              </p>
                              <Show when={Object.keys(call().preview.args).length > 0}>
                                <pre class="max-h-64 overflow-auto rounded-md bg-inset p-2 font-mono text-xs wrap-break-word whitespace-pre-wrap text-primary-text">
                                  {JSON.stringify(call().preview.args, null, 2)}
                                </pre>
                              </Show>
                            </div>
                          )}
                        </Match>

                        <Match when={isActionConfigAction(data()) && (data() as ActionConfigAction)}>
                          {(action) => <ChiefActionConfig action={action()} pending={isPending()} />}
                        </Match>

                        <Match when={data().actionKind === "action_set_enabled" && (data() as ActionSetEnabledAction)}>
                          {(toggle) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="text-sm text-primary-text">
                                {t(
                                  toggle().preview.enabled
                                    ? "confirm_action_card.action_enable_line"
                                    : "confirm_action_card.action_disable_line",
                                  { name: toggle().preview.actionName ?? toggle().preview.actionId }
                                )}
                              </p>
                            </div>
                          )}
                        </Match>

                        <Match when={data().actionKind === "action_dry_run" && (data() as ActionDryRunAction)}>
                          {(dryRun) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-sm font-bold text-primary-text">
                                {dryRun().preview.actionName ?? dryRun().preview.actionId}
                              </p>
                              <p class="text-xs text-muted-text">{t("confirm_action_card.dry_run_note")}</p>
                            </div>
                          )}
                        </Match>

                        <Match when={isChiefDeleteAction(data()) && (data() as ChiefDeleteAction)}>
                          {(del) => <ChiefDelete action={del()} />}
                        </Match>

                        <Match
                          when={
                            (data().actionKind === "label_create" || data().actionKind === "smart_tag_create") &&
                            (data() as LabelCreateAction)
                          }
                        >
                          {(label) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <div class="flex min-w-0 items-center gap-2">
                                <Show when={label().preview.color}>
                                  <span
                                    class="size-2.5 shrink-0 rounded-full"
                                    style={{ "background-color": label().preview.color }}
                                    aria-hidden="true"
                                  />
                                </Show>
                                <p class="truncate text-sm font-bold text-primary-text">{label().preview.name}</p>
                              </div>
                              <Show when={label().preview.description}>
                                <p class="line-clamp-2 text-xs wrap-break-word whitespace-pre-wrap text-muted-text">
                                  {label().preview.description}
                                </p>
                              </Show>
                            </div>
                          )}
                        </Match>

                        <Match
                          when={
                            (data().actionKind === "label_assign" || data().actionKind === "label_unassign") &&
                            (data() as LabelAssignAction | LabelUnassignAction)
                          }
                        >
                          {(assign) => <ChiefLabelAssets action={assign()} />}
                        </Match>

                        <Match when={data().actionKind === "asset_update" && (data() as AssetUpdateAction)}>
                          {(update) => <ChiefAssetUpdate preview={update().preview} />}
                        </Match>

                        <Match
                          when={
                            !isGmailAction(data()) &&
                            data().actionKind !== "calendar_move" &&
                            !isSlackAction(data()) &&
                            !isNotionAction(data()) &&
                            !isLinearAction(data()) &&
                            !isGitHubAction(data()) &&
                            !isMCPAction(data()) &&
                            !isGDriveAction(data()) &&
                            !isChiefAction(data()) &&
                            (data() as CalendarAction)
                          }
                        >
                          {(cal) => (
                            <div class="min-w-0 flex-1 space-y-1 self-start">
                              <p class="truncate text-sm font-bold text-primary-text">{cal().preview.title}</p>
                              <Show when={calendarWhen(cal().preview)}>
                                <p class="text-xs text-muted-text">{calendarWhen(cal().preview)}</p>
                              </Show>
                              <Show when={cal().preview.location}>
                                <span class="inline-flex max-w-full min-w-0 items-center gap-1 text-xs text-muted-text">
                                  <TbMapPin class="size-3.5 shrink-0" />
                                  <span class="truncate">{cal().preview.location}</span>
                                </span>
                              </Show>
                              <Show when={nonEmpty(cal().preview.attendees)}>
                                {(attendees) => (
                                  <span class="inline-flex items-center gap-1 text-xs text-muted-text">
                                    <TbUsers class="size-3.5 shrink-0" />
                                    <span class="truncate">{attendees().join(", ")}</span>
                                  </span>
                                )}
                              </Show>
                              <Show when={cal().preview.addMeet}>
                                <span class="inline-flex items-center gap-1 text-xs text-muted-text">
                                  <TbVideo class="size-3.5 shrink-0" />
                                  {t("confirm_action_card.meet_line")}
                                </span>
                              </Show>
                              <Show when={cal().preview.recurrence?.length}>
                                <span class="inline-flex items-center gap-1 text-xs text-muted-text">
                                  <TbRepeat class="size-3.5 shrink-0" />
                                  {t("confirm_action_card.repeats_line")}
                                </span>
                              </Show>
                              <Show when={cal().preview.calendarId}>
                                <span class="inline-flex max-w-full min-w-0 items-center gap-1 text-xs text-muted-text">
                                  <TbCalendarEvent class="size-3.5 shrink-0" />
                                  <span class="truncate">
                                    {t("confirm_action_card.calendar_label", {
                                      calendar: cal().preview.calendarId ?? "",
                                    })}
                                  </span>
                                </span>
                              </Show>
                            </div>
                          )}
                        </Match>
                      </Switch>

                      <Show when={isPending() && !resolving()}>
                        <div class="hidden w-px self-stretch bg-primary-border @md:block" aria-hidden="true" />
                        <div class="flex shrink-0 flex-col gap-2 self-start @md:items-end @md:self-center">
                          <div class="flex flex-row flex-wrap gap-2 @md:justify-end">
                            <Show when={approvable()}>
                              <button
                                type="button"
                                onClick={() => void submit("approve")}
                                disabled={isSubmitting()}
                                aria-busy={isSubmitting()}
                                class="flex items-center justify-center gap-2 rounded-md bg-brand px-3 py-1.5 text-sm font-semibold text-on-brand transition-colors hover:bg-brand-hover focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <Show when={isSubmitting() && !submittingAlways()}>
                                  <TbLoader2 class="size-4 animate-spin" />
                                </Show>
                                <span>
                                  {isSubmitting() && !submittingAlways()
                                    ? t("confirm_action_card.sending")
                                    : t("confirm_action_card.approve")}
                                </span>
                              </button>
                            </Show>
                            <Show when={alwaysApproveOp()}>
                              {(op) => (
                                <button
                                  type="button"
                                  onClick={() => void submit("approve_always")}
                                  disabled={isSubmitting()}
                                  aria-busy={isSubmitting() && submittingAlways()}
                                  class="flex items-center justify-center gap-2 rounded-md border border-primary-border px-3 py-1.5 text-sm font-medium text-primary-text transition-colors hover:bg-panel-hover focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  <Show when={isSubmitting() && submittingAlways()}>
                                    <TbLoader2 class="size-4 animate-spin" />
                                  </Show>
                                  <span>{alwaysAllowLabels(op(), mcpServerName(), t).button}</span>
                                </button>
                              )}
                            </Show>
                            <button
                              type="button"
                              onClick={() => void submit("reject")}
                              disabled={isSubmitting()}
                              class="flex items-center justify-center gap-2 rounded-md border border-primary-border px-3 py-1.5 text-sm font-medium text-primary-text transition-colors hover:bg-panel-hover focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {t("confirm_action_card.reject")}
                            </button>
                            <Show when={approvable() && isEditable(data())}>
                              <button
                                type="button"
                                onClick={() => setEditing(true)}
                                disabled={isSubmitting()}
                                class="flex items-center justify-center gap-2 rounded-md border border-primary-border px-3 py-1.5 text-sm font-medium text-primary-text transition-colors hover:bg-panel-hover focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {t("confirm_action_card.edit")}
                              </button>
                            </Show>
                          </div>
                          <Show when={!approvable()}>
                            <p class="text-xs text-secondary-text @md:text-right">
                              {t("confirm_action_card.proposer_only")}
                            </p>
                          </Show>
                          <Show when={alwaysApproveOp()}>
                            <Show
                              when={rt.actions.openToolPermissions}
                              fallback={
                                <p class="text-xs text-secondary-text @md:text-right">
                                  {t("confirm_action_card.always_allow_hint")}
                                </p>
                              }
                            >
                              <button
                                type="button"
                                onClick={() => rt.actions.openToolPermissions?.()}
                                class="text-left text-xs text-secondary-text underline-offset-2 transition-colors hover:text-primary-text hover:underline focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none @md:text-right"
                              >
                                {t("confirm_action_card.always_allow_hint")}
                              </button>
                            </Show>
                          </Show>
                          <Show
                            when={approvable() && hasNotifyToggle(data()) && (data() as CalendarAction | RsvpAction)}
                          >
                            {(cal) => (
                              <label class="flex cursor-pointer items-center gap-2 text-xs text-secondary-text">
                                <input
                                  type="checkbox"
                                  class="size-4 rounded border-primary-border text-brand focus-visible:ring-2 focus-visible:ring-brand"
                                  checked={notifyChecked(cal())}
                                  disabled={isSubmitting()}
                                  onChange={(e) => setNotifyAttendees(e.currentTarget.checked)}
                                />
                                {t("confirm_action_card.notify_attendees")}
                              </label>
                            )}
                          </Show>
                        </div>
                      </Show>
                    </div>
                  </Show>
                </div>
              </div>

              <Switch>
                <Match when={isPending() && !resolving() && editing()}>
                  <ConfirmActionEdit
                    payload={data()}
                    notify={hasNotifyToggle(data()) ? notifyChecked(data() as CalendarAction) : false}
                    onNotifyChange={setNotifyAttendees}
                    isSubmitting={isSubmitting()}
                    error={error()}
                    onApprove={(editedParams) => void submit("approve", editedParams)}
                    onCancel={() => setEditing(false)}
                  />
                </Match>

                <Match when={status() === "running"}>
                  <p class="mt-3 inline-flex items-center gap-1.5 text-sm text-muted-text">
                    <TbLoader2 class="size-4 shrink-0 animate-spin" />
                    {t("confirm_action_card.running")}
                  </p>
                </Match>

                <Match when={status() === "executed"}>
                  <p class="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-green-600 dark:text-green-400">
                    <TbCheck class="size-4 shrink-0" />
                    {executedMessage(data())}
                    <Show when={settledAt()}>
                      {(at) => <span class="text-xs font-normal text-muted-text">{at()}</span>}
                    </Show>
                  </p>
                </Match>

                <Match when={status() === "rejected"}>
                  <p class="mt-3 inline-flex items-center gap-1.5 text-sm text-muted-text">
                    <TbX class="size-4 shrink-0" />
                    {t("confirm_action_card.rejected")}
                  </p>
                </Match>

                <Match when={status() === "failed"}>
                  <p class="mt-3 inline-flex items-center gap-1.5 text-sm text-red-500">
                    <TbAlertTriangle class="size-4 shrink-0" />
                    {failureMessage()}
                    <Show when={settledAt()}>{(at) => <span class="text-xs text-muted-text">{at()}</span>}</Show>
                  </p>
                  <Show when={reconnectServer()}>
                    {(server) => (
                      <div class="mt-2">
                        <McpReconnectButton serverId={server().serverId} serverName={server().serverName} />
                      </div>
                    )}
                  </Show>
                </Match>

                <Match when={status() === "handled"}>
                  <p class="mt-3 inline-flex items-center gap-1.5 text-sm text-muted-text">
                    <TbCheck class="size-4 shrink-0" />
                    {t("confirm_action_card.already_handled")}
                  </p>
                </Match>
              </Switch>
              <Show when={permissionNote()}>
                {(note) => (
                  <div class="mt-2">
                    <PermissionNote text={note()} />
                  </div>
                )}
              </Show>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
